/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { VueConstructor } from 'vue';
import sensors from 'sa-sdk-javascript';

export interface TrackingProps {
  $title?: string;
  $referrer?: string;
  $url?: string;
  $url_path?: string;
  utm_source?: string | null;
  desc?: string | null;
  describe?: string | null;
  abtest?: string | null;
  feature_flags?: string | null;
}

function defaultProps(): TrackingProps {
  return {
    $title: document.title,
    $url: window.location.href,
    $url_path: window.location.pathname,
    $referrer: window.__vue_router_referrer__,
  };
}

/**
 * 创建自定义埋点方法
 * @param name 事件名
 * @param properties 传递的属性
 * @returns Promise<void>
 */
function createTracker<T extends TrackingProps>(name: string, properties: T): Promise<void> {
  return new Promise((resolve) => {
    sensors.track(
      name,
      {
        ...defaultProps(),
        ...properties,
      },
      () => {
        resolve();
      },
    );
  });
}

let initialized = false;

export interface SATrackingOptions {
  projectName: string;
  showLog?: boolean;
}

/**
 * 初始化神策SDK
 */
function init(options: SATrackingOptions) {
  if (initialized) return;

  sensors.init({
    server_url: 'https://sadata.yixin.com/sa?project=' + options.projectName,
    show_log: options.showLog ?? false,
    callback_timeout: 500,
    app_js_bridge: true,
    heatmap: {
      clickmap: 'default',
      scroll_notice_map: 'default',
      collect_element: function (element_target: HTMLElement) {
        if (
          element_target.getAttribute('disabled') != null ||
          element_target.getAttribute('data-sa-disabled') != null
        ) {
          return false;
        }

        return true;
      },
      custom_property: function (element_target: HTMLElement) {
        const properties: { desc?: string; describe?: string } = {};

        const desc = element_target.getAttribute('data-sa-desc');
        if (desc != null) {
          properties.desc = desc;
        }

        const describe = element_target.getAttribute('data-sa-describe');
        if (describe != null) {
          properties.describe = describe;
        }

        const placeholder = element_target.getAttribute('placeholder');
        if (
          element_target.tagName.toLowerCase() === 'input' &&
          placeholder != null &&
          properties.desc == null
        ) {
          properties.desc = placeholder;
        }

        return properties;
      },
    },
  });

  initialized = true;
}

/**
 * 神策登录用户ID
 */
function login(id?: string | number): Promise<void> {
  return new Promise((resolve) => {
    if (!initialized) {
      resolve();
      return;
    }

    sensors.login(String(id), () => {
      resolve();
    });
  });
}

/**
 * 神策退出登录
 */
function logout() {
  if (!initialized) return;
  sensors.logout();
}

/**
 * 注册全局参数
 */
function registerPage(properties: Record<string, any>) {
  if (!initialized) return;
  // registerPage的属性会执行合并操作，不会覆盖已设置的属性
  sensors.registerPage(properties);
}

/**
 * 清除全局参数
 */
function clearPageRegister() {
  if (!initialized) return;
  // @ts-ignore
  sensors.clearPageRegister(true);
}

/**
 * 弹窗事件埋点
 */
function trackPopup(
  properties: {
    popup_name?: string;
    popup_desc?: string;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('popup', properties);
}

/**
 * PV事件埋点
 */
function trackPageView(properties?: TrackingProps & { is_finished?: 1 | 0 }) {
  if (!initialized) return;
  // 暂时去掉PageLeave插件，因为目前暂时没有用，但产生了大量事件导致费用上涨
  // sensors.use('PageLeave', { custom_props: properties });
  sensors.quick('autoTrackSinglePage', properties);
}

/**
 * 子页面事件埋点
 */
function trackSubPageView(
  properties: {
    status_name?: string;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('child_page', properties);
}

/**
 * 接口结果埋点
 */
function trackInterfaceResults(properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('interface_results', properties);
}

/**
 * 登录事件埋点
 */
function trackLogin(
  properties: {
    is_new?: boolean | null;
    is_login?: boolean;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('registe', {
    ...properties,
    is_login: true, // 固定传值，数据团队要求的
  });
}

/**
 * 输入内容变更埋点
 */
function trackInputChange(
  properties: {
    custom_element_type: string;
    custom_element_name: string;
    custom_element_class_name: string | null;
    custom_element_selector?: string | null;
  } & TrackingProps,
): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker('change', properties);
}

/**
 * 点击埋点
 * 仅采集除 a, input, button,textarea 之外的标签
 */
function trackClick(target: HTMLElement, properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return new Promise((resolve) => {
    sensors.quick('trackHeatMap', target, properties, () => {
      resolve();
    });
  });
}

/**
 * 点击埋点
 * 采集所有标签
 */
function trackAllClick(target: HTMLElement, properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return new Promise((resolve) => {
    sensors.quick('trackAllHeatMap', target, properties, () => {
      resolve();
    });
  });
}

/**
 * 滑动区域埋点
 */
function trackSwipe(properties: TrackingProps): Promise<void> {
  if (!initialized) return Promise.resolve();
  return createTracker<TrackingProps>('swipe', properties);
}

/**
 * 根据DOM节点获取选择器路径
 */
function getSelector(el: Element): string {
  // @ts-ignore
  return sensors.heatmap.getDomSelector(el);
}

/**
 * 序列化JSON数据
 */
function serialize(data: any) {
  try {
    return JSON.stringify(data);
  } catch {
    return '';
  }
}

const SATracking = {
  init,
  login,
  logout,
  registerPage,
  clearPageRegister,
  trackPopup,
  trackClick,
  trackAllClick,
  trackPageView,
  trackSubPageView,
  trackInterfaceResults,
  trackLogin,
  trackInputChange,
  trackSwipe,
  getSelector,
  serialize,
};

export function SATrackingPlugin(Vue: VueConstructor) {
  Vue.prototype.$tracking = SATracking;
}

export default SATracking;
